import { useState } from 'react'
import { Send, CheckCircle2, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'

interface ContactCardProps {
  title: string
  onOpenChat?: (initialMessage?: string) => void
}

export function ContactCard({ title, onOpenChat }: ContactCardProps) {
  const [submitted, setSubmitted] = useState(false)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (onOpenChat) {
      onOpenChat(message || `Hi, I am inquiring about "${title}". Is it available?`)
    } else {
      setSubmitted(true)
    }
  }

  return (
    <Card className="p-6 rounded-2xl glass border border-white/10 space-y-4">
      <h3 className="font-display text-lg font-bold text-white">Inquire About This Listing</h3>
      <p className="text-xs text-muted-foreground">Send a direct message to the verified dealer or seller.</p>

      {submitted ? (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium space-y-1">
          <div className="flex items-center gap-2 font-bold">
            <CheckCircle2 className="h-4 w-4" /> Inquiry Sent!
          </div>
          <p>The seller will get back to you within 24 hours.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">Your Name</label>
            <Input
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-white/5 border-white/10 text-xs text-white"
              required
            />
          </div>

          <div>
            <label className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">Phone Number</label>
            <Input
              placeholder="+1 (555) 000-0000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="bg-white/5 border-white/10 text-xs text-white"
              required
            />
          </div>

          <div>
            <label className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">Message</label>
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Is this ${title} still available?`}
              className="w-full rounded-md bg-white/5 border border-white/10 p-2.5 text-xs text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary"
              required
            />
          </div>

          <div className="space-y-2 pt-1">
            <Button type="submit" size="sm" className="w-full bg-primary text-white hover:bg-primary/90 glow-md font-bold">
              Send Inquiry <Send className="ml-1.5 h-3.5 w-3.5" />
            </Button>
            {onOpenChat && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChat()}
                className="w-full border-primary/30 text-primary hover:bg-primary/10 font-bold text-xs"
              >
                <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Start Instant Live Chat
              </Button>
            )}
          </div>
        </form>
      )}
    </Card>
  )
}
